<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import { format, parseISO, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';

const props = defineProps({
  inboxOptions: {
    type: Array,
    default: () => [],
  },
  selectedInbox: {
    type: Object,
    default: null,
  },
  selectedPeriod: {
    type: Object,
    default: null,
  },
  dateRange: {
    type: Array,
    default: () => [],
  },
  isCustomDateRange: {
    type: Boolean,
    default: false,
  },
  authHeaders: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'update:selectedInbox',
  'update:selectedPeriod',
  'update:dateRange',
  'update:isCustomDateRange',
  'filter-change',
  'data-loaded',
]);

const STORAGE_KEY = 'relatorio_filtros_state';

const { t } = useI18n();
const { accountId } = useAccount();

// Loading state
const isLoading = ref(false);

// API response data
const analysisData = ref(null);

// Grouping options (removed periodOptions prop)
const groupingOptions = [
  { id: 'day', name: 'Diário' },
  { id: 'week', name: 'Semanal' },
  { id: 'month', name: 'Mensal' },
  { id: 'year', name: 'Anual' },
  { id: 'custom', name: 'Personalizado' },
];

// Referência local para rastrear alterações de data
const isUserChangingDates = ref(false);

// Calculate date ranges based on grouping
const calculateDateRangeFromGrouping = (groupingId) => {
  const today = endOfDay(new Date());
  let startDate;

  switch (groupingId) {
    case 'day':
      startDate = startOfDay(subDays(today, 1)); // Yesterday to today
      break;
    case 'week':
      startDate = startOfDay(subDays(today, 7)); // 7 days ago to today
      break;
    case 'month':
      startDate = startOfDay(subDays(today, 30)); // 30 days ago to today
      break;
    case 'year':
      startDate = startOfDay(subDays(today, 365)); // 365 days ago to today
      break;
    default:
      startDate = startOfDay(subDays(today, 7)); // Default to last 7 days
  }

  return [startDate, today];
};


const earliestInteractionDate = ref(null); // Data da interação mais antiga
const displayDate = ref(null); // Data para exibir na mensagem

// Format date to YYYY-MM-DD for API
const formatDateForAPI = (date) => {
  return format(date, 'yyyy-MM-dd');
};

// Fetch data from API
const fetchDashboardData = async () => {
  if (!accountId.value || !props.dateRange || props.dateRange.length !== 2) {
    return;
  }

  isLoading.value = true;

  try {
    const payload = {
      account_id: accountId.value,
      start_date: formatDateForAPI(props.dateRange[0]),
      end_date: formatDateForAPI(props.dateRange[1]),
    };

    // Add inbox_id if not "all"
    if (props.selectedInbox && props.selectedInbox.id !== 'all') {
      payload.inbox_id = props.selectedInbox.id;
    }

    const response = await axios.post(`/api/v1/ouroboros_dashboard`, payload, {
      headers: props.authHeaders
    });
    analysisData.value = response.data;
    
    // Extract earliest interaction date from response
    if (response.data && response.data.earliest_interaction_date) {
      earliestInteractionDate.value = response.data.earliest_interaction_date;
      displayDate.value = response.data.earliest_interaction_date;
    } else {
      // Use start_date as fallback when API returns null
      displayDate.value = props.dateRange[0];
    }
    
    // Salvar displayDate no localStorage
    salvarEstadoFiltros({
      selectedInbox: props.selectedInbox,
      selectedPeriod: props.selectedPeriod,
      dateRange: props.dateRange,
      isCustomDateRange: props.isCustomDateRange,
    });
    

    
    // Emit the loaded data to parent component
    emit('data-loaded', response.data);
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    analysisData.value = null;
  } finally {
    isLoading.value = false;
  }
};

// Converter opções para o formato esperado pelo FilterSelect
const inboxSelectOptions = computed(() => {
  return props.inboxOptions.map(option => ({
    value: option.id,
    label: option.name,
    icon: option.id === 'all' ? 'i-lucide-inbox' : 'i-lucide-message-square',
  }));
});

const groupingSelectOptions = computed(() => {
  return groupingOptions
    .filter(option => option.id !== 'custom' || props.isCustomDateRange) // Only show custom when it's active
    .map(option => {
      const iconMap = {
        day: 'i-lucide-calendar-days',
        week: 'i-lucide-calendar-range',
        month: 'i-lucide-calendar-check',
        year: 'i-lucide-calendar-heart',
        custom: 'i-lucide-calendar-clock',
      };
      return {
        value: option.id,
        label: option.name,
        icon: iconMap[option.id] || 'i-lucide-calendar',
        disabled: option.id === 'custom' && !props.isCustomDateRange,
      };
    });
});

// Valores selecionados para o FilterSelect
const selectedInboxValue = computed({
  get: () => props.selectedInbox?.id || 'all',
  set: value => {
    const selected = props.inboxOptions.find(option => option.id === value);
    if (selected) {
      updateInbox(selected);
    }
  },
});

const selectedGroupingValue = computed({
  get: () => {
    if (props.isCustomDateRange) return 'custom';
    return props.selectedPeriod?.id || 'day';
  },
  set: value => {
    if (value === 'custom') return; // Don't allow selecting custom directly
    
    const selected = groupingOptions.find(option => option.id === value);
    if (selected) {
      updateGrouping(selected);
    }
  },
});

// Recuperar estado dos filtros do localStorage
const recuperarEstadoFiltros = () => {
  try {
    const estadoSalvo = localStorage.getItem(STORAGE_KEY);
    if (estadoSalvo) {
      const parsed = JSON.parse(estadoSalvo);

      // Converter as strings de data de volta para objetos Date
      if (parsed.dateRange && Array.isArray(parsed.dateRange)) {
        parsed.dateRange = [
          parseISO(parsed.dateRange[0]),
          parseISO(parsed.dateRange[1]),
        ];
      } else {
        // Default to daily grouping
        parsed.dateRange = calculateDateRangeFromGrouping('day');
      }

      // Recuperar displayDate se existir
      if (parsed.displayDate) {
        displayDate.value = parseISO(parsed.displayDate);
      }

      return parsed;
    }
  } catch (error) {
    console.error('Erro ao recuperar o estado dos filtros:', error);
  }

  // Estado padrão se não houver nada salvo - daily grouping
  const defaultDateRange = calculateDateRangeFromGrouping('day');

  return {
    dateRange: defaultDateRange,
    selectedInbox: { id: 'all', name: 'Todos os Canais' },
    selectedPeriod: { id: 'day', name: 'Diário' },
    isCustomDateRange: false,
  };
};

// Salvar estado dos filtros no localStorage
const salvarEstadoFiltros = estado => {
  try {
    // Armazenar as datas como strings ISO
    const estadoParaSalvar = {
      ...estado,
      dateRange: estado.dateRange
        ? [estado.dateRange[0].toISOString(), estado.dateRange[1].toISOString()]
        : undefined,
      displayDate: displayDate.value
        ? (displayDate.value instanceof Date ? displayDate.value.toISOString() : displayDate.value)
        : undefined,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(estadoParaSalvar));
  } catch (error) {
    console.error('Erro ao salvar o estado dos filtros:', error);
  }
};

// Inicializar o componente com dados do localStorage
onMounted(async () => {
  const estadoSalvo = recuperarEstadoFiltros();

  // Emitir eventos para atualizar os valores nos componentes pais
  // Sempre usar os valores do localStorage, mas apenas se eles existirem
  if (estadoSalvo.selectedInbox) {
    emit('update:selectedInbox', estadoSalvo.selectedInbox);
  }

  if (estadoSalvo.selectedPeriod) {
    emit('update:selectedPeriod', estadoSalvo.selectedPeriod);
  }

  if (estadoSalvo.dateRange && estadoSalvo.dateRange.length === 2) {
    emit('update:dateRange', estadoSalvo.dateRange);
  }

  // Atualizar flag de data personalizada
  if (estadoSalvo.isCustomDateRange !== undefined) {
    emit('update:isCustomDateRange', estadoSalvo.isCustomDateRange);
  }

  // Notificar o componente pai sobre a mudança
  emit('filter-change', {
    inbox: estadoSalvo.selectedInbox,
    period: estadoSalvo.selectedPeriod,
    dateRange: estadoSalvo.dateRange,
    isCustomDate: estadoSalvo.isCustomDateRange,
  });

  // Aguardar o próximo tick para garantir que as props foram atualizadas
  await nextTick();
  
  // Fetch initial data from API usando os dados salvos
  // Como as props podem não estar atualizadas ainda, vamos fazer a chamada diretamente
  if (accountId.value && estadoSalvo.dateRange && estadoSalvo.dateRange.length === 2) {
    isLoading.value = true;

    try {
      const payload = {
        account_id: accountId.value,
        start_date: formatDateForAPI(estadoSalvo.dateRange[0]),
        end_date: formatDateForAPI(estadoSalvo.dateRange[1]),
      };

      // Add inbox_id if not "all"
      if (estadoSalvo.selectedInbox && estadoSalvo.selectedInbox.id !== 'all') {
        payload.inbox_id = estadoSalvo.selectedInbox.id;
      }

      const response = await axios.post(`/api/v1/ouroboros_dashboard`, payload, {
        headers: props.authHeaders
      });
      
      analysisData.value = response.data;
      
      // Extract earliest interaction date from response
      if (response.data && response.data.earliest_interaction_date) {
        earliestInteractionDate.value = response.data.earliest_interaction_date;
        displayDate.value = response.data.earliest_interaction_date;
      } else {
        // Use start_date as fallback when API returns null
        displayDate.value = estadoSalvo.dateRange[0];
      }
      
      // Emit the loaded data to parent component
      emit('data-loaded', response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      analysisData.value = null;
    } finally {
      isLoading.value = false;
    }
  }
});

// Helpers para atualizar os valores
const updateInbox = async (value) => {
  emit('update:selectedInbox', value);

  // Salvar no localStorage
  salvarEstadoFiltros({
    selectedInbox: value,
    selectedPeriod: props.selectedPeriod,
    dateRange: props.dateRange,
    isCustomDateRange: props.isCustomDateRange,
  });

  emit('filter-change', {
    inbox: value,
    period: props.selectedPeriod,
    dateRange: props.dateRange,
    isCustomDate: props.isCustomDateRange,
  });

  // Aguarda o próximo tick para garantir que as props foram atualizadas
  await nextTick();
  
  // Fetch new data with updated filter
  await fetchDashboardData();
};

const updateGrouping = async (value) => {
  // Calculate new date range based on grouping
  const newDateRange = calculateDateRangeFromGrouping(value.id);
  
  emit('update:selectedPeriod', value);
  emit('update:dateRange', newDateRange);
  emit('update:isCustomDateRange', false);

  // Salvar no localStorage
  salvarEstadoFiltros({
    selectedInbox: props.selectedInbox,
    selectedPeriod: value,
    dateRange: newDateRange,
    isCustomDateRange: false,
  });

  emit('filter-change', {
    inbox: props.selectedInbox,
    period: value,
    dateRange: newDateRange,
    isCustomDate: false,
  });

  // Aguarda o próximo tick para garantir que as props foram atualizadas
  await nextTick();
  
  // Fetch new data with updated filter
  await fetchDashboardData();
};

const updateDateRange = async (value) => {
  isUserChangingDates.value = true;
  emit('update:dateRange', value);

  // When user changes dates manually, activate custom mode
  const customPeriod = { id: 'custom', name: 'Personalizado' };
  emit('update:selectedPeriod', customPeriod);
  emit('update:isCustomDateRange', true);

  // Salvar no localStorage
  salvarEstadoFiltros({
    selectedInbox: props.selectedInbox,
    selectedPeriod: customPeriod,
    dateRange: value,
    isCustomDateRange: true,
  });

  // Emite evento com flag isCustomDate = true
  emit('filter-change', {
    inbox: props.selectedInbox,
    period: customPeriod,
    dateRange: value,
    isCustomDate: true,
  });

  // Aguarda o próximo tick para garantir que as props foram atualizadas
  await nextTick();
  
  // Fetch new data with updated filter
  await fetchDashboardData();
};

// Formatar data para exibição
const formatDateRange = computed(() => {
  if (!props.dateRange || props.dateRange.length !== 2) return '';

  const startDate = format(props.dateRange[0], 'PP', { locale: ptBR });
  const endDate = format(props.dateRange[1], 'PP', { locale: ptBR });

  return `${startDate} - ${endDate}`;
});

// Calcular informações contextuais para o filtro de período
const periodDescription = computed(() => {
  if (props.isCustomDateRange && props.dateRange && props.dateRange.length === 2) {
    const startDate = format(props.dateRange[0], 'dd/MM/yyyy');
    const endDate = format(props.dateRange[1], 'dd/MM/yyyy');
    return `Período de ${startDate} até ${endDate}`;
  }

  if (!props.selectedPeriod) return '';

  const descriptions = {
    day: 'Ontem até hoje',
    week: '7 dias atrás até hoje',
    month: '30 dias atrás até hoje',
    year: '365 dias atrás até hoje',
    custom: 'Período personalizado',
  };

  return descriptions[props.selectedPeriod.id] || '';
});

// Mudar o ícone conforme o período selecionado
const periodIcon = computed(() => {
  if (!props.selectedPeriod) return 'i-lucide-calendar';

  const icons = {
    day: 'i-lucide-calendar-days',
    week: 'i-lucide-calendar-range',
    month: 'i-lucide-calendar-check',
    year: 'i-lucide-calendar-heart',
    custom: 'i-lucide-calendar-clock',
  };

  return icons[props.selectedPeriod.id] || 'i-lucide-calendar';
});
</script>

<template>
  <div>
    <div class="mb-6">
      <div class="flex flex-col md:flex-row gap-4 mb-2">
        <div class="w-full md:w-1/3">
          <p class="mb-2 text-xs font-medium flex items-center">
            <span class="i-lucide-inbox mr-1 text-n-slate-11" />
            Canal
          </p>
          <FilterSelect
            v-model="selectedInboxValue"
            :options="inboxSelectOptions"
            placeholder="Selecione um canal"
          />
        </div>

        <div class="w-full md:w-1/3">
          <p class="mb-2 text-xs font-medium flex items-center">
            <span class="i-lucide-calendar mr-1 text-n-slate-11" />
            Período
          </p>
          <WootDateRangePicker
            show-range
            :value="dateRange"
            confirm-text="Aplicar"
            :placeholder="formatDateRange || 'Selecione o período'"
            @change="updateDateRange"
          />
        </div>

        <div class="w-full md:w-1/3">
          <p class="mb-2 text-xs font-medium flex items-center">
            <span class="mr-1 text-n-slate-11" :class="[periodIcon]" />
            Agrupamento
          </p>
          <FilterSelect
            v-model="selectedGroupingValue"
            :options="groupingSelectOptions"
            placeholder="Selecione o agrupamento"
          />
        </div>
      </div>

      <!-- Descrição do período -->
      <div
        v-if="periodDescription"
        class="flex items-center mt-2 text-xs text-n-slate-11"
      >
        <span class="mr-1" :class="[periodIcon]" />
        {{ periodDescription }}
      </div>
    </div>

    <!-- Mensagem de início dos dados -->
    <div v-if="displayDate" class="mb-6 p-3 bg-n-slate-1 rounded-lg border border-n-weak">
      <p class="text-sm flex items-center text-n-slate-11 mb-0">
        <span class="i-lucide-calendar-clock mr-2 text-n-blue-text"></span>
        Os dados apresentados têm como base registros a partir de {{ format(displayDate instanceof Date ? displayDate : new Date(displayDate), 'dd/MM/yyyy', { locale: ptBR }) }}, incluindo todo o período selecionado para análise.
      </p>
    </div>

    <!-- Loading indicator -->
    <div v-if="isLoading" class="flex items-center mt-2 text-xs text-n-slate-11">
      <span class="i-lucide-loader-2 mr-1 animate-spin" />
      Carregando dados...
    </div>
  </div>
</template>
